import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dateUtils";
import { useConfirm } from "@/components/ConfirmDialog";
import ContractEditDialog from "@/components/ContractEditDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, FileText, Package, Buildings, Truck, CheckCircle, Warning, XCircle,
  FilePdf, FileArrowUp, TrashSimple, DownloadSimple, CalendarBlank, User, PencilSimple, LinkSimple,
} from "@phosphor-icons/react";

export default function ContractDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const askConfirm = useConfirm();
  const [contract, setContract] = useState(null);
  const [deliveries, setDeliveries] = useState({ groups: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [attLabel, setAttLabel] = useState("");
  const [confOpen, setConfOpen] = useState(false);
  const [confForm, setConfForm] = useState({
    delivery_address: "", contact_person: "", contact_phone: "",
    delivery_date_text: "", shipping_note: "",
    payment_type: "", payment_terms_text: "", additional_notes: "",
  });
  const [savingConf, setSavingConf] = useState(false);
  // Canlı PDF önizleme (Teyit dialog'u içinde)
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  // Sözleşmeyi düzenle dialog'u
  const [editOpen, setEditOpen] = useState(false);
  // Kalıcı önizleme cache-busting anahtarı — kaydet sonrası iframe'i yeniden yükletir
  const [pdfKey, setPdfKey] = useState(0);
  const canManage = !!user;

  const load = async () => {
    setLoading(true);
    try {
      const [c, d] = await Promise.all([
        axios.get(`${API}/contracts/${id}`),
        axios.get(`${API}/contracts/${id}/deliveries`),
      ]);
      setContract(c.data);
      setDeliveries(d.data);
      // confirmation_data'yı forma yansıt
      const cd = c.data.confirmation_data || {};
      setConfForm({
        delivery_address: cd.delivery_address || "",
        contact_person: cd.contact_person || "",
        contact_phone: cd.contact_phone || "",
        delivery_date_text: cd.delivery_date_text || "",
        shipping_note: cd.shipping_note || "",
        payment_type: cd.payment_type || "",
        payment_terms_text: cd.payment_terms_text || "",
        additional_notes: cd.additional_notes || "",
      });
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Canlı PDF önizleme — dialog açık iken form değiştikçe 600ms debounce ile yeniden üret
  useEffect(() => {
    if (!confOpen) return;
    let cancelled = false;
    let createdUrl = "";
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const r = await axios.post(
          `${API}/contracts/${id}/order-confirmation-preview.pdf`,
          confForm,
          { responseType: "blob" },
        );
        if (cancelled) return;
        createdUrl = URL.createObjectURL(r.data);
        setPreviewUrl((old) => {
          if (old) { try { URL.revokeObjectURL(old); } catch { /* noop */ } }
          return createdUrl;
        });
      } catch (e) {
        if (!cancelled) toast.error(formatErr(e));
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [confOpen, confForm, id]);

  // Dialog kapanınca blob URL'i temizle
  useEffect(() => {
    if (confOpen) return;
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch { /* noop */ }
      setPreviewUrl("");
    }
  }, [confOpen]);

  const downloadPdf = () => {
    window.open(`${API}/contracts/${id}/order-confirmation.pdf`, "_blank");
  };

  const copyConfirmationLink = async () => {
    // Sözleşme token yoksa PDF endpoint'ini bir kez tetikleyerek ürettir
    let tok = contract?.confirmation_token;
    if (!tok) {
      try {
        // Token'ı üretmek için PDF endpoint'ini "prime" et (GET içindeki _ensure_confirmation_token)
        await axios.get(`${API}/contracts/${id}/order-confirmation.pdf`, { responseType: "blob" });
        const r = await axios.get(`${API}/contracts/${id}`);
        tok = r.data.confirmation_token;
        setContract(r.data);
      } catch (e) {
        toast.error(formatErr(e));
        return;
      }
    }
    if (!tok) { toast.error("Teyit bağlantısı üretilemedi."); return; }
    const url = `${window.location.origin}/teyit/${tok}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Teyit bağlantısı panoya kopyalandı");
    } catch {
      // Fallback: yeni sekmede aç
      window.open(url, "_blank");
    }
  };

  const saveConfirmationData = async () => {
    setSavingConf(true);
    try {
      await axios.patch(`${API}/contracts/${id}/confirmation-data`, confForm);
      toast.success("Sipariş Teyit Formu bilgileri kaydedildi");
      setConfOpen(false);
      await load();
      setPdfKey(k => k + 1);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setSavingConf(false); }
  };
  const setC = (k, v) => setConfForm(f => ({ ...f, [k]: v }));

  const uploadAttachment = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Sadece PDF dosyası yüklenebilir");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (attLabel.trim()) fd.append("label", attLabel.trim());
      await axios.post(`${API}/contracts/${id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Ek yüklendi");
      setAttLabel("");
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setUploading(false); }
  };

  const removeAttachment = async (aid, name) => {
    const ok = await askConfirm({
      title: "Ek Sil",
      description: `"${name}" ekini silmek istiyor musunuz?`,
      destructive: true,
      confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/contracts/${id}/attachments/${aid}`);
      toast.success("Ek silindi");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (loading || !contract) {
    return (
      <Shell title="Sözleşme Detay" subtitle="Yükleniyor..." hideSiteSelector>
        <div className="py-16 text-center text-slate-400">Yükleniyor...</div>
      </Shell>
    );
  }

  const items = contract.items || [];
  const items_progress = contract.items_progress || items;
  const pct = Number(contract.progress_percent || 0);
  const isMulti = items.length > 0;

  return (
    <Shell
      title={contract.code}
      subtitle={`Sözleşme Detayı${contract.source_rfq_code ? ` · Kaynak ${contract.source_rfq_code}` : ""}`}
      hideSiteSelector
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => nav("/sozlesmeler")} data-testid="ct-back"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest font-bold border-slate-300">
            <ArrowLeft size={14} className="mr-1.5" /> Sözleşmelere Dön
          </Button>
          {canManage && (
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="ct-edit-contract"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest font-bold border-[#95C11F] text-[#4E7A0F] hover:bg-[#95C11F] hover:text-white">
              <PencilSimple size={14} className="mr-1.5" /> Sözleşmeyi Düzenle
            </Button>
          )}
          {canManage && (
            <Button variant="outline" onClick={copyConfirmationLink} data-testid="ct-copy-confirm-link"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest font-bold border-slate-300">
              <LinkSimple size={14} className="mr-1.5" /> Teyit Bağlantısını Kopyala
            </Button>
          )}
          <Button onClick={downloadPdf} data-testid="ct-pdf-download"
                  className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153a63] text-white text-xs uppercase tracking-widest font-bold">
            <FilePdf size={14} className="mr-1.5" /> Sipariş Teyit Formu (PDF)
          </Button>
        </div>
      }
    >
      {/* Teyit Durumu Banner'ı (tedarikçi teyit ettiyse veya reddettiyse) */}
      {(contract.confirmation_status === "confirmed" || contract.confirmation_status === "rejected") && (
        <div
          data-testid="ct-confirm-banner"
          className={`mb-4 p-4 border rounded-none sm:rounded-sm flex items-start gap-3 ${
            contract.confirmation_status === "confirmed"
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-rose-50 border-rose-200 text-rose-900"
          }`}
        >
          {contract.confirmation_status === "confirmed"
            ? <CheckCircle size={28} weight="fill" />
            : <XCircle size={28} weight="fill" />}
          <div className="flex-1">
            <div className="font-bold text-sm uppercase tracking-widest">
              {contract.confirmation_status === "confirmed" ? "Tedarikçi Teyit Etti" : "Düzeltme Talep Edildi"}
            </div>
            <div className="text-xs mt-1">
              {contract.confirmation_confirmed_by_name || "-"} • {fmtDate(contract.confirmation_action_at)}
            </div>
            {contract.confirmation_status === "rejected" && contract.confirmation_correction_fields?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ct-correction-chips">
                {contract.confirmation_correction_fields.map((k) => {
                  const labelMap = {
                    price: "Fiyat", quantity: "Miktar",
                    delivery_date: "Teslim Tarihi", payment_terms: "Ödeme Koşulu",
                    address: "Adres", other: "Diğer",
                  };
                  return (
                    <span key={k} className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 bg-white/70 border border-rose-300 text-rose-800 rounded-none sm:rounded-sm">
                      {labelMap[k] || k}
                    </span>
                  );
                })}
              </div>
            )}
            {contract.confirmation_note && (
              <div className="text-sm mt-2 italic">&quot;{contract.confirmation_note}&quot;</div>
            )}
          </div>
        </div>
      )}

      {/* Genel bilgi */}
      <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <InfoBlock label="Firma" icon={<Buildings size={16} className="text-slate-400" />} value={contract.supplier_name} />
          <InfoBlock label="Şantiye" icon={<Package size={16} className="text-slate-400" />} value={contract.site_name} />
          <InfoBlock label="Durum" icon={<CheckCircle size={16} className="text-slate-400" />}
                     value={
                       <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${
                         contract.status === "aktif" ? "bg-emerald-100 text-emerald-700"
                         : contract.status === "tamamlandi" ? "bg-slate-200 text-slate-600"
                         : "bg-red-100 text-red-700"
                       }`}>{contract.status}</span>
                     } />
          <InfoBlock label="Başlangıç" icon={<CalendarBlank size={16} className="text-slate-400" />} value={fmtDate(contract.start_date) || "-"} />
          <InfoBlock label="Bitiş / Teslim" icon={<CalendarBlank size={16} className="text-slate-400" />} value={fmtDate(contract.end_date) || "-"} />
          {contract.total_amount_incl_vat > 0 && (
            <InfoBlock label="Genel Toplam (KDV Dahil)" icon={<FileText size={16} className="text-slate-400" />}
                       value={`₺${Number(contract.total_amount_incl_vat).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`}
                       highlight />
          )}
          {(contract.payment_terms || contract.payment_term_days > 0) && (
            <InfoBlock label="Ödeme Koşulu" value={contract.payment_terms || `${contract.payment_term_days} gün vade`} />
          )}
          {(contract.shipping_included !== undefined) && (
            <InfoBlock label="Nakliye" icon={<Truck size={16} className="text-slate-400" />}
                       value={contract.shipping_included ? "Fiyata dahil" : `Tedarikçiye ait${contract.shipping_unit_price > 0 ? ` (₺${contract.shipping_unit_price} × ${contract.shipping_quantity} ${contract.shipping_unit})` : ""}`} />
          )}
        </div>
      </Card>

      {/* Kalemler + Genel İlerleme */}
      <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-600">
            Kalemler ({isMulti ? items.length : 1})
          </div>
          <div className="text-xs font-mono text-slate-700">
            <strong>{(contract.delivered_qty || 0).toLocaleString("tr-TR")}</strong>
            <span className="text-slate-400"> / {(contract.total_qty ?? contract.total_quantity ?? 0).toLocaleString("tr-TR")} {contract.unit}</span>
            <span className={`ml-2 font-bold ${pct >= 100 ? "text-emerald-700" : "text-[#1B4776]"}`}>%{pct.toFixed(1)}</span>
          </div>
        </div>
        <div className="w-full h-3 bg-slate-100 border border-slate-200 mb-4">
          <div className={`h-full transition-all ${pct >= 100 ? "bg-emerald-500" : pct >= 75 ? "bg-[#95C11F]" : "bg-[#1B4776]"}`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>

        <div className="space-y-3">
          {(isMulti ? items_progress : [{
            material_name: contract.material_name,
            unit: contract.unit,
            quantity: contract.total_quantity,
            unit_price: contract.unit_price,
            vat_rate: contract.vat_rate,
            delivered_qty: contract.delivered_qty,
            progress_percent: contract.progress_percent,
            delivery_count: contract.delivery_count,
          }]).map((it, i) => {
            const iqty = Number(it.quantity || 0);
            const idel = Number(it.delivered_qty || 0);
            const ipct = Number(it.progress_percent || (iqty > 0 ? idel / iqty * 100 : 0));
            const group = deliveries.groups?.[i] || { deliveries: [] };
            return (
              <div key={i} className="border border-slate-200" data-testid={`ct-detail-item-${i}`}>
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-bold text-sm text-slate-800">{it.material_name}</div>
                    <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                      {iqty.toLocaleString("tr-TR")} {it.unit}
                      {it.unit_price > 0 && ` × ₺${Number(it.unit_price).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`}
                      {it.vat_rate > 0 && ` · KDV %${it.vat_rate}`}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs font-mono">
                      <strong className={ipct >= 100 ? "text-emerald-700" : "text-slate-700"}>{idel.toLocaleString("tr-TR")}</strong>
                      <span className="text-slate-400"> / {iqty.toLocaleString("tr-TR")}</span>
                    </div>
                    <div className="text-[10px] font-bold text-slate-500">%{ipct.toFixed(1)} · {group.deliveries.length} teslim</div>
                  </div>
                </div>
                {/* Teslimat geçmişi */}
                {group.deliveries.length > 0 ? (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-widest text-slate-500 bg-white border-b border-slate-100">
                        <th className="text-left px-3 py-1.5 font-bold">Tarih</th>
                        <th className="text-left px-3 py-1.5 font-bold">İrs. No</th>
                        <th className="text-right px-3 py-1.5 font-bold">Miktar</th>
                        <th className="text-left px-3 py-1.5 font-bold">Personel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.deliveries.map((d, j) => (
                        <tr key={d.id} className={j % 2 ? "bg-slate-50/40" : "bg-white"}>
                          <td className="px-3 py-1.5 font-mono">{fmtDate(d.delivery_date)}</td>
                          <td className="px-3 py-1.5 font-mono text-slate-600">{d.delivery_note_no || "-"}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{Number(d.quantity).toLocaleString("tr-TR")} {d.unit}</td>
                          <td className="px-3 py-1.5 text-slate-600 text-[11px]">{d.created_by_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="px-3 py-3 text-center text-xs text-slate-400 italic">Bu kalem için henüz teslimat yok.</div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Ekler */}
      <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-600">
            Ekler ({(contract.attachments || []).length})
          </div>
        </div>
        {canManage && (
          <div className="flex flex-wrap items-end gap-2 mb-3 p-3 border border-dashed border-slate-300 bg-slate-50/50">
            <div className="flex-1 min-w-[180px]">
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Ek Etiketi (opsiyonel)</label>
              <Input
                value={attLabel}
                onChange={(e) => setAttLabel(e.target.value)}
                placeholder="Örn: Sipariş Teyit Formu — İmzalı"
                data-testid="ct-att-label"
                className="mt-1 rounded-none sm:rounded-sm h-9"
              />
            </div>
            <div>
              <input
                type="file"
                accept="application/pdf,.pdf"
                id="ct-att-file"
                className="hidden"
                onChange={(e) => uploadAttachment(e.target.files?.[0])}
              />
              <label
                htmlFor="ct-att-file"
                className={`inline-flex items-center gap-1.5 h-9 px-3 text-xs uppercase tracking-widest font-bold cursor-pointer border ${
                  uploading
                    ? "bg-slate-200 text-slate-400 border-slate-200 cursor-not-allowed"
                    : "bg-[#95C11F] hover:bg-[#7ea617] text-[#1B2A3A] border-[#95C11F]"
                }`}
                data-testid="ct-att-upload-btn"
              >
                <FileArrowUp size={14} />
                {uploading ? "Yükleniyor..." : "PDF Ek Yükle"}
              </label>
            </div>
          </div>
        )}
        {(contract.attachments || []).length === 0 ? (
          <div className="text-center py-6 text-xs text-slate-400 italic" data-testid="ct-no-attachments">
            Henüz ek yok. Sipariş Teyit Formu&apos;nun imzalı halini veya karşı taraftan gelen sözleşme PDF&apos;ini yükleyebilirsiniz.
          </div>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-200">
            {(contract.attachments || []).map((att) => (
              <div key={att.id} className="flex items-center gap-3 px-3 py-2" data-testid={`ct-att-${att.id}`}>
                <FilePdf size={20} weight="duotone" className="text-red-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{att.label || "Sözleşme PDF"}</div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
                    <span className="truncate max-w-[280px]">{att.original_name}</span>
                    <span className="text-slate-300">·</span>
                    <span className="flex items-center gap-1"><User size={10} /> {att.uploaded_by_name}</span>
                    <span className="text-slate-300">·</span>
                    <span className="font-mono">{fmtDate(att.uploaded_at)}</span>
                  </div>
                </div>
                <a
                  href={`${API.replace(/\/api$/, "")}${att.path}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`ct-att-download-${att.id}`}
                  className="text-xs uppercase tracking-widest font-bold text-[#1B4776] hover:text-[#153a63] inline-flex items-center gap-1"
                >
                  <DownloadSimple size={14} /> İndir
                </a>
                {canManage && (
                  <button
                    onClick={() => removeAttachment(att.id, att.original_name)}
                    data-testid={`ct-att-delete-${att.id}`}
                    className="text-red-600 hover:text-red-800"
                    title="Sil"
                  >
                    <TrashSimple size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Sipariş Teyit Formu — Kalıcı Canlı Önizleme */}
      <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4" data-testid="ct-live-preview-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-600">
              Sipariş Teyit Formu — Canlı Önizleme
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              Sözleşmeyi veya teyit bilgilerini kaydettiğinizde bu önizleme otomatik olarak yenilenir.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPdfKey(k => k + 1)}
                    data-testid="ct-preview-refresh"
                    className="rounded-none sm:rounded-sm text-[10px] uppercase tracking-widest font-bold border-slate-300 h-8">
              Yenile
            </Button>
            <Button size="sm" onClick={downloadPdf}
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153a63] text-white text-[10px] uppercase tracking-widest font-bold h-8">
              <FilePdf size={12} className="mr-1" /> İndir
            </Button>
          </div>
        </div>
        <div className="border border-slate-200 bg-slate-100 h-[700px]">
          <iframe
            key={pdfKey}
            title="Sipariş Teyit Formu Önizlemesi"
            src={`${API}/contracts/${id}/order-confirmation.pdf?inline=1&v=${pdfKey}`}
            data-testid="ct-live-preview-iframe"
            className="w-full h-full border-0 bg-white"
          />
        </div>
      </Card>

      {contract.notes && (
        <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-600 mb-2">Notlar</div>
          <div className="text-sm text-slate-700 whitespace-pre-line">{contract.notes}</div>
        </Card>
      )}

      {(contract.status !== "aktif") && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 mb-4">
          <Warning size={14} weight="fill" />
          Sözleşme durumu: <strong>{contract.status}</strong>
        </div>
      )}


      {/* Sözleşmeyi Düzenle dialog'u (multi-item + supplier autocomplete + dates) */}
      <ContractEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        contract={contract}
        onSaved={() => {
          setEditOpen(false);
          load();
          setPdfKey(k => k + 1);  // canlı önizleme iframe'i yeniden yükle
        }}
      />
    </Shell>
  );
}

const InfoBlock = ({ label, icon, value, highlight }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5">
      {icon}{label}
    </div>
    <div className={`mt-1 text-sm ${highlight ? "font-mono font-bold text-[#1B4776]" : "text-slate-800"}`}>
      {value || "-"}
    </div>
  </div>
);
